// Modular Tsoro Rules Engine — Zimbabwean Standard Tsoro (Fuva / Imbwe)
// Reference: sections 1–16 of the supplied rules document.
//
// Board representation (board[row][col]):
//   row 0 = B outer (top, furthest from B)        ← wait, "outer" = furthest from center = closest to player
//   Actually: row 0 = B outer (closest to B / farthest from centre)
//   row 1 = B inner
//   row 2 = A inner
//   row 3 = A outer (closest to A / farthest from centre)
//
// Player A sits at bottom, owns rows [2, 3].
// Player B sits at top, owns rows [0, 1].
// Capture pairing: A_inner[c] (row 2, col c) faces B_inner[c] (row 1, col c)
//   so when A lands in empty A_inner[c], A captures B_inner[c] and (if it had counters) B_outer[c].

export const ROWS = 4;
export const COLS = 8;
export const PLAYER_A = "A";
export const PLAYER_B = "B";

// Anticlockwise sowing routes — each player's own two rows form a loop.
// Player A: outer row left→right (cols 0..7), then inner row right→left (7..0).
const ROUTE_A_CCW = [
  [3, 0], [3, 1], [3, 2], [3, 3], [3, 4], [3, 5], [3, 6], [3, 7],
  [2, 7], [2, 6], [2, 5], [2, 4], [2, 3], [2, 2], [2, 1], [2, 0],
];
// Player B: outer row right→left (cols 7..0), then inner row left→right (0..7).
const ROUTE_B_CCW = [
  [0, 7], [0, 6], [0, 5], [0, 4], [0, 3], [0, 2], [0, 1], [0, 0],
  [1, 0], [1, 1], [1, 2], [1, 3], [1, 4], [1, 5], [1, 6], [1, 7],
];
// Clockwise routes are the reverse traversal of the same loop.
const ROUTE_A_CW = [...ROUTE_A_CCW].reverse();
const ROUTE_B_CW = [...ROUTE_B_CCW].reverse();

export const DIRECTION_CCW = "CCW";
export const DIRECTION_CW = "CW";

export function routeFor(player, direction = DIRECTION_CCW) {
  if (direction === DIRECTION_CW) {
    return player === PLAYER_A ? ROUTE_A_CW : ROUTE_B_CW;
  }
  return player === PLAYER_A ? ROUTE_A_CCW : ROUTE_B_CCW;
}

export function ownsRow(player, row) {
  if (player === PLAYER_A) return row === 2 || row === 3;
  return row === 0 || row === 1;
}

export function innerRowOf(player) {
  return player === PLAYER_A ? 2 : 1;
}
export function outerRowOf(player) {
  return player === PLAYER_A ? 3 : 0;
}
export function oppInnerOf(player) {
  return player === PLAYER_A ? 1 : 2;
}
export function oppOuterOf(player) {
  return player === PLAYER_A ? 0 : 3;
}
export function opponentOf(player) {
  return player === PLAYER_A ? PLAYER_B : PLAYER_A;
}

export function emptyBoard() {
  return Array.from({ length: ROWS }, () => Array(COLS).fill(0));
}

export function cloneBoard(b) {
  return b.map((r) => r.slice());
}

export function createInitialState(rulesetId = "zimbabwe-standard", { sowingDirection = DIRECTION_CCW } = {}) {
  const board = emptyBoard();
  // Standard Zimbabwean setup: 2 counters in every outer-row pit, inner rows empty.
  for (let c = 0; c < COLS; c++) {
    board[0][c] = 2; // B outer
    board[3][c] = 2; // A outer
  }
  return {
    rulesetId,
    version: 1,
    board,
    currentPlayer: PLAYER_A,
    sowingDirection,
    phase: "NORMAL",
    captures: { A: 0, B: 0 },
    status: "ACTIVE", // ACTIVE | WON | DRAWN
    winner: null,
    endReason: null,
    turnNumber: 1,
    moveNumber: 0,
    history: [],
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };
}

export function countersOnBoardOf(board, player) {
  const rows = player === PLAYER_A ? [2, 3] : [0, 1];
  let s = 0;
  for (const r of rows) for (let c = 0; c < COLS; c++) s += board[r][c];
  return s;
}

export function ownPits(board, player) {
  const rows = player === PLAYER_A ? [2, 3] : [0, 1];
  const pits = [];
  for (const r of rows) for (let c = 0; c < COLS; c++) {
    if (board[r][c] > 0) pits.push([r, c]);
  }
  return pits;
}

export function getPhaseFor(board, player) {
  const own = ownPits(board, player);
  if (own.length === 0) return "NORMAL";
  const anyMulti = own.some(([r, c]) => board[r][c] >= 2);
  return anyMulti ? "NORMAL" : "SINGLETON";
}

function routeIndex(player, r, c, direction) {
  const route = routeFor(player, direction);
  for (let i = 0; i < route.length; i++) {
    if (route[i][0] === r && route[i][1] === c) return i;
  }
  return -1;
}

export function nextPitOnRoute(player, r, c, direction = DIRECTION_CCW) {
  const route = routeFor(player, direction);
  const i = routeIndex(player, r, c, direction);
  return route[(i + 1) % route.length];
}

export function getLegalMoves(state) {
  const { board, currentPlayer, sowingDirection } = state;
  const phase = getPhaseFor(board, currentPlayer);
  const own = ownPits(board, currentPlayer);
  const legal = [];
  if (phase === "SINGLETON") {
    for (const [r, c] of own) {
      if (board[r][c] !== 1) continue;
      const [nr, nc] = nextPitOnRoute(currentPlayer, r, c, sowingDirection);
      if (board[nr][nc] === 0) legal.push([r, c]);
    }
  } else {
    for (const [r, c] of own) {
      if (board[r][c] >= 2) legal.push([r, c]);
    }
  }
  return legal;
}

export function isLegalMove(state, r, c) {
  return getLegalMoves(state).some(([rr, cc]) => rr === r && cc === c);
}

// Total counter conservation check helper (for tests).
export function totalCounters(state) {
  let s = state.captures.A + state.captures.B;
  for (let r = 0; r < ROWS; r++) for (let c = 0; c < COLS; c++) s += state.board[r][c];
  return s;
}

/**
 * Apply a move: pick up the contents of (r,c), sow anticlockwise around the
 * player's two rows, perform relays, then resolve capture if the final landing
 * is an empty inner-row pit.
 *
 * Returns { state, events } where events is an ordered log suitable for
 * driving animation. The new state is the canonical result; the animation must
 * not change game logic.
 */
export function applyMove(state, r, c) {
  if (state.status !== "ACTIVE") throw new Error("GAME_NOT_ACTIVE");
  if (!ownsRow(state.currentPlayer, r)) throw new Error("NOT_OWN_PIT");
  if (!isLegalMove(state, r, c)) throw new Error("ILLEGAL_MOVE");

  const player = state.currentPlayer;
  const opp = opponentOf(player);
  const direction = state.sowingDirection || DIRECTION_CCW;
  const route = routeFor(player, direction);
  const board = cloneBoard(state.board);
  const events = [];

  events.push({ type: "TURN_STARTED", player, turn: state.turnNumber });
  events.push({ type: "PIT_SELECTED", from: [r, c], count: board[r][c] });

  let inHand = board[r][c];
  board[r][c] = 0;
  events.push({ type: "COUNTERS_PICKED_UP", from: [r, c], count: inHand });

  let idx = routeIndex(player, r, c, direction);
  let lastPit = [r, c];
  let relays = 0;

  // Sow with relay until landing in a previously-empty pit.
  // Guard against pathological infinite loops (max iterations ~ counters^2).
  let safety = 0;
  const SAFETY_MAX = 10000;
  while (true) {
    while (inHand > 0) {
      idx = (idx + 1) % route.length;
      const [nr, nc] = route[idx];
      board[nr][nc] += 1;
      inHand -= 1;
      events.push({ type: "COUNTER_SOWN", to: [nr, nc] });
      lastPit = [nr, nc];
      safety++;
      if (safety > SAFETY_MAX) throw new Error("SOWING_SAFETY_BREAK");
    }
    const [lr, lc] = lastPit;
    // "previously empty" means before the last drop the pit was empty,
    // i.e. its current count is exactly 1.
    if (board[lr][lc] === 1) break;

    // Relay: pick everything up (including the counter we just placed) and continue.
    inHand = board[lr][lc];
    board[lr][lc] = 0;
    relays += 1;
    events.push({ type: "RELAY_STARTED", at: [lr, lc], count: inHand });
    idx = routeIndex(player, lr, lc, direction);
  }

  // Capture check: final landing in player's own inner row, against opponent's column.
  const innerRow = innerRowOf(player);
  let capturedTotal = 0;
  const capturedPits = [];
  if (lastPit[0] === innerRow) {
    const col = lastPit[1];
    const oi = oppInnerOf(player);
    const oo = oppOuterOf(player);
    if (board[oi][col] > 0) {
      events.push({ type: "CAPTURE_TRIGGERED", column: col, byLanding: [lastPit[0], lastPit[1]] });
      const n1 = board[oi][col];
      board[oi][col] = 0;
      capturedTotal += n1;
      capturedPits.push({ pit: [oi, col], count: n1 });
      events.push({ type: "COUNTERS_CAPTURED", from: [oi, col], count: n1 });
      if (board[oo][col] > 0) {
        const n2 = board[oo][col];
        board[oo][col] = 0;
        capturedTotal += n2;
        capturedPits.push({ pit: [oo, col], count: n2 });
        events.push({ type: "COUNTERS_CAPTURED", from: [oo, col], count: n2 });
      }
    }
  }

  const captures = { A: state.captures.A, B: state.captures.B };
  captures[player] += capturedTotal;

  let status = "ACTIVE";
  let winner = null;
  let endReason = null;

  // Win: all opponent counters captured.
  if (countersOnBoardOf(board, opp) === 0) {
    status = "WON";
    winner = player;
    endReason = "ALL_OPPONENT_COUNTERS_CAPTURED";
    events.push({ type: "GAME_WON", player, reason: endReason });
  }

  const nextPlayer = opp;
  const nextPhase = getPhaseFor(board, nextPlayer);

  if (status === "ACTIVE") {
    const probe = { ...state, board, currentPlayer: nextPlayer, phase: nextPhase };
    const nextLegal = getLegalMoves(probe);
    if (nextLegal.length === 0) {
      // Default Zimbabwean alt rule: opponent who cannot move loses.
      status = "WON";
      winner = player;
      endReason = "OPPONENT_NO_LEGAL_MOVE";
      events.push({ type: "NO_LEGAL_MOVE", player: nextPlayer });
      events.push({ type: "GAME_WON", player, reason: endReason });
    }
  }

  if (nextPhase !== state.phase) {
    events.push({ type: "PHASE_CHANGED", phase: nextPhase, player: nextPlayer });
  }
  events.push({
    type: "TURN_ENDED",
    player,
    landingPit: lastPit,
    captured: capturedTotal,
    relays,
  });

  const historyEntry = {
    player,
    from: [r, c],
    landingPit: lastPit,
    captured: capturedTotal,
    capturedPits,
    relays,
    prevBoard: state.board,
    prevCaptures: state.captures,
    prevPhase: state.phase,
    prevCurrentPlayer: state.currentPlayer,
    prevStatus: state.status,
    prevWinner: state.winner,
    prevEndReason: state.endReason,
    prevTurnNumber: state.turnNumber,
    events,
  };

  return {
    state: {
      ...state,
      board,
      currentPlayer: status === "ACTIVE" ? nextPlayer : state.currentPlayer,
      phase: nextPhase,
      captures,
      status,
      winner,
      endReason,
      turnNumber: status === "ACTIVE" ? state.turnNumber + 1 : state.turnNumber,
      moveNumber: state.moveNumber + 1,
      history: [...state.history, historyEntry],
      updatedAt: Date.now(),
    },
    events,
  };
}

export function undoMove(state) {
  if (!state.history || state.history.length === 0) return state;
  const last = state.history[state.history.length - 1];
  return {
    ...state,
    board: last.prevBoard,
    currentPlayer: last.prevCurrentPlayer,
    phase: last.prevPhase,
    captures: last.prevCaptures,
    status: last.prevStatus,
    winner: last.prevWinner,
    endReason: last.prevEndReason,
    turnNumber: last.prevTurnNumber,
    moveNumber: Math.max(0, state.moveNumber - 1),
    history: state.history.slice(0, -1),
    updatedAt: Date.now(),
  };
}

// Plain-language summary of a move (last history entry).
export function describeMove(entry, direction = DIRECTION_CCW) {
  if (!entry) return "";
  const p = entry.player === PLAYER_A ? "Player 1" : "Player 2";
  const [lr] = entry.landingPit;
  const dir = direction === DIRECTION_CW ? "clockwise" : "anticlockwise";
  const inner = entry.player === PLAYER_A ? lr === 2 : lr === 1;
  const parts = [];
  parts.push(`${p} moved ${dir} starting from pit ${describePit(entry.player, entry.from)}.`);
  if (entry.relays > 0) {
    parts.push(`The sowing relayed ${entry.relays} time${entry.relays === 1 ? "" : "s"}.`);
  }
  parts.push(`The final counter landed in ${describePit(entry.player, entry.landingPit)}.`);
  if (entry.captured > 0) {
    parts.push(`${entry.captured} opposing counter${entry.captured === 1 ? "" : "s"} captured.`);
  } else if (inner) {
    parts.push("No capture: the opposing inner pit was empty.");
  } else {
    parts.push("The turn ended in an outer pit, so no capture occurred.");
  }
  return parts.join(" ");
}

export function describePit(player, [r, c]) {
  const row = r === 2 || r === 1 ? "inner" : "outer";
  const owner =
    (player === PLAYER_A && (r === 2 || r === 3)) || (player === PLAYER_B && (r === 0 || r === 1))
      ? "own"
      : "opponent";
  return `${owner} ${row} pit ${c + 1}`;
}

// Compact position code: rows ordered top-to-bottom (B-outer, B-inner, A-inner, A-outer)
// counter per pit, dashes between rows; followed by current player.
// Example: "22222222-00000000-00000000-22222222:A"
export function encodePosition(state) {
  const rows = state.board
    .map((row) => row.map((n) => Math.min(n, 35).toString(36)).join(""))
    .join("-");
  return `${rows}:${state.currentPlayer}`;
}

export function decodePosition(code) {
  const [rowsPart, player] = code.split(":");
  if (!rowsPart || !player) throw new Error("INVALID_POSITION_CODE");
  const rows = rowsPart.split("-");
  if (rows.length !== ROWS) throw new Error("INVALID_POSITION_CODE_ROWS");
  const board = rows.map((rowStr) => {
    if (rowStr.length !== COLS) throw new Error("INVALID_POSITION_CODE_COLS");
    return rowStr.split("").map((ch) => parseInt(ch, 36));
  });
  if (board.some((row) => row.some((n) => Number.isNaN(n) || n < 0))) {
    throw new Error("INVALID_POSITION_CODE_VALUES");
  }
  if (player !== PLAYER_A && player !== PLAYER_B) throw new Error("INVALID_POSITION_CODE_PLAYER");
  return { board, currentPlayer: player };
}
